import React, { useEffect, useState, useMemo } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import Button from '../components/button';
import Dropdown from '../components/dropdown';
import Select from '../components/select';
import Input from '../components/input';
import Error from '../components/error_message';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import scrollToElement from 'helpers/scroll_to_element';
import useGym from 'helpers/use_gym';
import FeatureNames from 'constants/feature_names';

const GymEmailOverrides = () => {
  const $http = useAngularService('$http');
  const $stateParams = useAngularService('$stateParams');
  const GTMService = useAngularService('GTMService');
  const Restangular = useAngularService('Restangular');
  const AdminUserService = useAngularService('AdminUserService');
  const FeatureAvailabilityService = useAngularService('FeatureAvailabilityService');
  const [initialScrolled, setInitialScrolled] = useState(false);

  const gym = useGym();

  const EmailOverrides = useMemo(() => Restangular.one('gyms', gym.id).all('email_overrides'), []);
  const highlightedType = useMemo(() => $stateParams.type, [$stateParams.type]);

  const [pageParams, setPageParams] = useState({ filter: '' });
  const [overridesMeta, setOverridesMeta] = useState<any>({});
  const [overrides, setOverrides] = useState<any>();
  const [overridesPairs, setOverridesPairs] = useState([]);
  const [overridesData, setOverridesData] = useState();

  const adminHasPermission = AdminUserService.has;

  const scrollToSection = () => {
    if (highlightedType && !initialScrolled) {
      scrollToElement(`panel-${highlightedType}`, { offset: -100 });
      setInitialScrolled(true);
    }
  };

  const filtersOptions = [
    ['', 'All emails'],
    ['booking', 'Booking emails'],
    ['membership', 'Membership / Payment emails'],
    ['account', 'Clubworx Account emails'],
    ['sequence_emails', `${FeatureNames.SEQUENCE} emails`],
    ['other', 'Other'],
  ];

  const getFilteredLabel = () => {
    const option = _.find(filtersOptions, (item) => item[0] === pageParams.filter);
    return option[1];
  };

  const filteredGroups = {
    booking: [
      'booking_daily_gym_digest',
      'booking_kiosk_checkin_alert_digest',
      'recurring_booking_notifications',
      'recurring_booking_activation_summaries',
    ],
    membership: [
      'membership_new_website_membership_purchase',
      'membership_notify_before_expiration',
      'membership_plan_specific_date_expiration',
      'invoice_failure_summary',
      'payments_general',
      'missed_class_make_up_class_allowance_digest',
      'ezidebit_customer_deactivation_notification',
      'membership_purchase_limit_notifications',
      'pos_sale_digest',
      'mobile_app_pos_sale_alert',
      'mobile_app_cancellation_requests',
      'mobile_app_suspension_requests',
    ],
    account: ['gym_sms_usage_alert', 'clubworx_invoice_email'],
    sequence_emails: [
      'sequence_digest',
      'inactive_sequence_summary',
      'sequence_payload_failure_digest',
    ],
    other: [
      'book_and_pay_digest',
      'sms_reply_notification',
      'report_export',
      'waiver_digest',
      'waiver_signed',
      'waiver_consent_request',
      'expired_prefilled_waiver_accessed',
      'website_join_duplicate',
      'website_join_email',
      'website_join_free_trial_email',
      'api_contact_created',
      'workout_performed_trainer_email',
      'bulk_import_change',
      'mobile_app_general',
      'inactive_sequence_summary',
      'late_grading_event_rsvp_email',
      'doorguard_unlock_success_digest',
      'doorguard_unlock_failure_digest',
    ],
  };

  const onFilterChanged = (value) => {
    setPageParams({ filter: value });
  };

  const loadMeta = () => {
    EmailOverrides.get('meta').then((data) => {
      setOverridesMeta(data);
    });
  };

  const createOverridePairs = () => {
    const pairs = _.chain(overridesMeta.email_info)
      .map((item, key) => [key, item])
      .filter((item) => {
        if (!pageParams.filter) return true;
        return filteredGroups[pageParams.filter].indexOf(item[0]) >= 0;
      })
      .pairs()
      .partition((item, index) => !(index % 2))
      .map((item) => _.object(item.map((i) => i[1])))
      .value();
    setOverridesPairs(pairs);

    setTimeout(() => scrollToSection(), 1000);
  };

  const createOverrides = () => {
    if (!overridesMeta || !overridesData) return false;

    const tempOverrides = {};
    _.each(
      overridesMeta.email_info,
      (val, key) =>
        (tempOverrides[key] = {
          email_type: key,
          selected_option: 'default',
        }),
    );

    _.each(overridesData, (item) => {
      tempOverrides[item.email_type] = item;

      tempOverrides[item.email_type].selected_option = item.do_not_send
        ? 'do_not_send'
        : item.send_to_default
        ? 'default'
        : 'other';
    });

    setOverrides(tempOverrides);
  };

  const loadOverrides = () => {
    EmailOverrides.getList({ count: 100 }).then((data) => {
      setOverridesData(data);
    });
  };

  useEffect(() => {
    GTMService.trackPage('Account Profile - Email Settings');
    loadMeta();
    loadOverrides();
  }, []);

  useEffect(() => {
    createOverrides();
  }, [overridesMeta, overridesData]);

  useEffect(() => {
    createOverridePairs();
  }, [overridesMeta, pageParams]);

  const updateOverride = (override, key) => {
    setOverrides({ ...overrides, [key]: override });
  };

  const onOptionSelect = (key: string, value: string) => {
    if (!adminHasPermission('admin_manage_email_settings')) return;

    const override = overrides[key];
    override.selected_option = value;

    switch (override.selected_option) {
      case 'default': {
        override.do_not_send = false;
        override.send_to_default = true;
        override.email_override = '';
        override.secondary_email_override = '';
        removeOverride(override);
        break;
      }
      case 'other': {
        override.send_to_default = false;
        override.do_not_send = false;
        override.edit = true;
        break;
      }
      case 'do_not_send': {
        override.send_to_default = false;
        override.do_not_send = true;
        override.email_override = '';
        override.secondary_email_override = '';
        saveOverride(override, key);
        break;
      }
    }
    updateOverride(override, key);
  };

  const removeOverride = (override) => {
    if (override.remove) {
      override.remove().then(() => {
        notification.success('Updated successfully');
        loadOverrides();
      });
    } else {
      notification.success('Updated successfully');
      loadOverrides();
    }
  };

  const onSaveClick = (override, key) => {
    if (
      override.send_to_default &&
      !override.email_override &&
      !override.secondary_email_override
    ) {
      removeOverride(override);
    } else {
      saveOverride(override, key);
    }
  };

  const saveOverride = (override, key) => {
    if (override.loading || !adminHasPermission('admin_manage_email_settings')) return;
    updateOverride({ ...override, loading: true, rootObjectName: 'gym_email_override' }, key);
    save(override).then(
      (response) => {
        const item = response.data ? response.data : response;

        GTMService.trackPage('Account Profile - Email Settings', 'change settings');
        notification.success('Updated successfully');
        updateOverride(
          {
            ...item,
            selected_option: item.do_not_send
              ? 'do_not_send'
              : item.send_to_default
              ? 'default'
              : 'other',
          },
          key,
        );
      },
      (response) => {
        updateOverride({ ...override, loading: false, errors: response.data.errors }, key);
      },
    );
  };

  const save: any = (override) => {
    override.rootObjectName = 'gym_email_override';
    if (override.id) {
      const url = `/gyms/${gym.id}/email_overrides/${override.id}`;
      return $http.put(url, { gym_email_override: override });
    } else {
      return EmailOverrides.post(override);
    }
  };

  const isAvailable = (override) => {
    return _.every(override.requirements, (requirement) => {
      switch (requirement) {
        case 'member_portal':
          return gym.member_portal_enabled;
        case 'waivers':
          return gym.gym_waiver_detail?.enabled;
        case 'pos':
          return FeatureAvailabilityService.isAvailable('pos');
        case 'workouts':
          return gym.workouts_enabled;
        case 'autopayments':
          return gym.has_payment_provider;
        case 'book_and_pay':
          return gym.book_and_pay_enabled;
        case 'attendance_feature':
          return FeatureAvailabilityService.isAvailable('attendance');
        case 'bulk_imports':
          return gym.gym_config.bulk_import_enabled;
        case 'mobile_app':
          return gym.mobile_app_enabled;
        case 'sequences':
          return gym.sequences_enabled;
        case 'ezidebit':
          return gym.uses_ezidebit;
        case 'recurring_bookings':
          return gym.gym_config.recurring_bookings_feature_enabled;
        case 'martial_arts':
          return gym.is_martial_arts;
        case 'doorguard':
          return gym.doorguard_enabled;
      }
    });
  };

  const defaultRecipient = (type) => {
    switch (type) {
      case 'current_user':
        return 'Current user';
      case 'contact_owner':
        return 'Contact owner (or Account owner if not present)';
      case 'event_instructors':
        return 'Event instructor(s)';
      default:
        return 'Account owner';
    }
  };

  if (!overridesPairs.length || !overridesData || !overrides) return null;

  return (
    <div className="content-box first-one">
      <h2 className="first-one">Clubworx email settings</h2>
      <p>
        On this page you can manage where email notifications are delivered for your
        account, or block emails if you do not wish to receive them.
        <br />
        <b>
          Account owner email (default for most notifications unless overridden): {gym.payor_email}
        </b>
      </p>
      <div className="form-group text-right">
        <label>Showing</label>{' '}
        <Dropdown
          options={filtersOptions.map((item) => ({ value: item[0], label: item[1] }))}
          onSelect={onFilterChanged}
          value={pageParams.filter}
        >
          {getFilteredLabel()}
        </Dropdown>
      </div>
      <div className="row">
        {overridesPairs.map((overridesColumn, index) => (
          <div className="col-md-6" key={index}>
            {_.map(
              overridesColumn,
              (overrideInfo, key) =>
                isAvailable(overrideInfo) && (
                  <div key={key}>
                    <div
                      className={classNames('panel', 'panel-default', {
                        'panel-disabled':
                          overrides && overrides[key].selected_option === 'do_not_send',
                        'panel--highlighted': key === highlightedType,
                      })}
                      id={`panel-${key}`}
                    >
                      <div className="panel-body">
                        <b>{overrideInfo.label}</b>
                        <br />
                        {overrideInfo.description}
                        <div className="form-group">
                          <Select
                            value={overrides[key].selected_option}
                            disabled={!adminHasPermission('admin_manage_email_settings')}
                            onChange={(value) => onOptionSelect(key, value as string)}
                            options={[
                              {
                                value: 'default',
                                label: `Send to ${defaultRecipient(
                                  overrideInfo.default_recipient,
                                )}`,
                              },
                              {
                                value: 'other',
                                label: 'Send to Other recipient(s)',
                              },
                              {
                                value: 'do_not_send',
                                label: 'Do not send this email',
                              },
                            ]}
                          />

                          {overrides[key].selected_option === 'default' &&
                            !overrides[key].email_override &&
                            !overrides[key].edit && (
                              <div className="margin-top-8">
                                <Button
                                  variant="link"
                                  onClick={() => {
                                    const override = {
                                      ...overrides[key],
                                      send_to_default: true,
                                      edit: true,
                                    };
                                    updateOverride(override, key);
                                  }}
                                >
                                  Click here to specify additional recipients
                                </Button>
                              </div>
                            )}
                        </div>

                        {overrides &&
                          (overrides[key].selected_option === 'other' ||
                            overrides[key].send_to_default) && (
                            <div>
                              {!overrides[key].edit && (
                                <div>
                                  <b>
                                    {overrides[key].send_to_default
                                      ? 'Also send to: '
                                      : overrides[key].email_override &&
                                        overrides[key].secondary_email_override
                                      ? 'Recipients: '
                                      : 'Recipient: '}
                                  </b>
                                  {overrides[key].email_override && (
                                    <span>{overrides[key].email_override}</span>
                                  )}
                                  {overrides[key].email_override &&
                                    overrides[key].secondary_email_override && <span>, </span>}
                                  {overrides[key].secondary_email_override && (
                                    <span>{overrides[key].secondary_email_override}</span>
                                  )}{' '}
                                  {adminHasPermission('admin_manage_email_settings') && (
                                    <Button
                                      variant="link"
                                      onClick={() => {
                                        updateOverride({ ...overrides[key], edit: true }, key);
                                      }}
                                    >
                                      Change
                                    </Button>
                                  )}
                                </div>
                              )}
                              {overrides[key].edit && (
                                <div className="form-group">
                                  <label>
                                    {overrides[key].send_to_default
                                      ? 'Also send to (optional)'
                                      : 'Recipients'}
                                  </label>
                                  <div className="form-group">
                                    <Input
                                      value={overrides[key].email_override}
                                      placeholder="#1 Email"
                                      onChange={(val) => {
                                        updateOverride(
                                          {
                                            ...overrides[key],
                                            email_override: val,
                                          },
                                          key,
                                        );
                                      }}
                                    />
                                    <Error error={overrides[key].errors?.email_override} />
                                  </div>
                                  <div className="form-group">
                                    <Input
                                      value={overrides[key].secondary_email_override}
                                      placeholder="#2 Email"
                                      onChange={(val) => {
                                        updateOverride(
                                          {
                                            ...overrides[key],
                                            secondary_email_override: val,
                                          },
                                          key,
                                        );
                                      }}
                                    />
                                    <Error
                                      error={overrides[key].errors?.secondary_email_override}
                                    />
                                  </div>
                                  {adminHasPermission('admin_manage_email_settings') && (
                                    <Button
                                      variant="primary"
                                      onClick={() => onSaveClick(overrides[key], key)}
                                    >
                                      Save
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

angular.module('gym').value('GymEmailOverrides', GymEmailOverrides);
